import { useEffect, useRef, useState } from "react";
import { useAdminLoading } from "../../../context/AdminLoadingContext";

interface TinyMCEProps {
  onChange: (content: string) => void;
  defaultValue?: string;
  showLink?: boolean;
  showImage?: boolean;
}

export default function TinyMCE({ onChange, defaultValue, showLink = false, showImage = false }: TinyMCEProps) {

  const editorRef = useRef<HTMLDivElement>(null);
  const [editorLoaded, setEditorLoaded] = useState(false);

  const { showLoading, hideLoading } = useAdminLoading();

  useEffect(() => {
    if (editorRef.current) {
      const script = document.createElement("script");
      script.src = "/tinymce/tinymce.min.js";
      script.onload = () => {
        const tinymce = (window as any).tinymce;
        const plugins: string[] = [
          "lists",
          "directionality",
          "fullscreen",
          "preview",
          "codesample",
          "table",
        ];
        if (showLink) plugins.push("link");
        if (showImage) plugins.push("image");

        // Always add link plugin for basic functionality
        if (!showLink) plugins.push("link");

        const toolbar = [
          "undo redo",
          "formatselect",
          "blocks",
          "bold italic",
          "textcolor",
          "alignleft aligncenter alignright",
          "bullist numlist lineheight",
          "table",
          "link",
          showImage ? "image" : "",
          "codesample",
          "fullscreen",
          "spellchecker",
        ]
          .filter(Boolean)
          .join(" | ");

        tinymce.init({
          target: editorRef.current,
          language: "fa",
          language_url: "/tinymce/langs/fa.js",
          skin_url: "/tinymce/skins/ui/oxide",
          content_css: "/tinymce/skins/ui/oxide/content.min.css",
          content_style: `
            @font-face {
            font-family: "IRANYekanX";
            src: url("/fonts/IRANYekanXFaNum-Medium.ttf") format("truetype");
            }
            body {
              font-size: 11pt;
              font-family: "IRANYekanX", sans-serif;
              direction: rtl !important;
              text-align: right !important;
            }
            p, div, h1, h2, h3, h4, h5, h6 {
              direction: rtl !important;
              text-align: right !important;
            }
            * {
              direction: rtl !important;
            }
            pre.language-markup, pre.language-css, pre.language-javascript, 
            pre.language-html, pre.language-xml, pre.language-json,
            code.language-markup, code.language-css, code.language-javascript,
            code.language-html, code.language-xml, code.language-json {
              direction: ltr !important;
              text-align: left !important;
              font-family: 'Courier New', monospace !important;
            }
          `,
          menubar: false,
          toolbar,
          plugins: plugins.join(" "),
          image_title: true,
          block_formats:
            "پاراگراف=p; سربرگ1=h1; سربرگ2=h2; سربرگ3=h3; سربرگ4=h4; سربرگ5=h5;",
          mobile: { toolbar_mode: "sliding" },
          toolbar_sticky: true,
          directionality: "rtl",
          default_link_target: "_blank",
          forced_root_block: "p",
          forced_root_block_attrs: {
            dir: "rtl",
            style: "text-align: right !important; direction: rtl !important;",
          },
          newline_behavior: "block",
          br_in_pre: false,
          automatic_uploads: true,

          base_url: "/tinymce",
          suffix: ".min",

          setup: (editor: any) => {
            editor.on("Change", () => {
              onChange(editor.getContent());
            });
            editor.on("init", function () {
              setEditorLoaded(true);
              if (defaultValue) {
                editor.setContent(defaultValue);
              }

              // Set RTL direction for the editor body
              const body = editor.getBody();
              if (body) {
                body.style.direction = "rtl";
                body.style.textAlign = "right";
              }
            });

            // Handle Enter key and new content for RTL
            editor.on("keydown", function (e: KeyboardEvent) {
              if (e.key === "Enter") {
                const currentNode = editor.selection.getNode();

                // Don't interfere with code blocks
                if (
                  currentNode.tagName === "PRE" ||
                  currentNode.closest("pre") ||
                  currentNode.classList?.contains("language-") ||
                  editor.selection.getContent().includes("<pre")
                ) {
                  return; // Let default behavior handle code blocks
                }

                e.preventDefault();

                // Insert a new paragraph with proper RTL attributes
                const content =
                  '<p dir="rtl" style="text-align: right !important; direction: rtl !important;"><br></p>';
                editor.insertContent(content);

                // Move cursor to the new paragraph
                const newP = editor.getBody().lastElementChild;
                if (newP) {
                  editor.selection.setCursorLocation(newP, 0);
                }
              }
            });

            // Handle all new content to ensure RTL (except code blocks)
            editor.on("BeforeSetContent", function (e: any) {
              if (e.content) {
                // Don't modify code blocks
                if (
                  !e.content.includes("<pre") &&
                  !e.content.includes("<code")
                ) {
                  // Ensure all paragraphs have RTL attributes
                  e.content = e.content.replace(
                    /<p>/g,
                    '<p dir="rtl" style="text-align: right !important; direction: rtl !important;">'
                  );
                  e.content = e.content.replace(
                    /<p([^>]*)>/g,
                    '<p$1 dir="rtl" style="text-align: right !important; direction: rtl !important;">'
                  );
                }
              }
            });

            // Handle paste events
            editor.on("paste", function (e: any) {
              setTimeout(() => {
                const body = editor.getBody();
                const paragraphs = body.querySelectorAll("p");
                paragraphs.forEach((p: HTMLElement) => {
                  p.setAttribute("dir", "rtl");
                  p.style.textAlign = "right";
                  p.style.direction = "rtl";
                });
              }, 10);
            });
          },
        });
      };
      document.body.appendChild(script);
    }
  }, []);
  

  useEffect(() => {
    const tinymce = (window as any).tinymce;
    if (editorLoaded && defaultValue && tinymce?.activeEditor) {
      tinymce.activeEditor.setContent(defaultValue);
    }
  }, [defaultValue, editorLoaded]);

  return <div ref={editorRef}></div>;
};